import { Component, OnInit, ViewChild, ElementRef, TemplateRef } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import Swal from 'sweetalert2';

import { APIService } from 'src/app/shared/service/apiService.service';
import { ProductsService } from 'src/app/shared/service/products.service';
import { MensajesService } from 'src/app/shared/service/mensajes.service';

@Component({
	selector: 'app-studentprofilemain',
	templateUrl: './studentprofilemain.component.html',
	styleUrls: ['./studentprofilemain.component.scss']
})
export class StudentprofilemainComponent implements OnInit {
	@ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
	@ViewChild('editProfileModal') editProfileModal!: TemplateRef<any>;

	// Estado base de UI
	menuAbierto = false;
	nombreMenuSeleccionado: string = 'Mi Perfil';
	editMode = false;
	selectedTab = 0;

	// Datos y formularios
	datosUsuario: any = {};
	idUsuario: number | null = null;
	passwordForm!: FormGroup;

	// Compras (placeholder inicial)
	saleOrders: any[] = [];
	filteredOrders: any[] = [];
	pagedOrders: any[] = [];
	pagina = 1;
	ordersPerPage = 10;
	totalPages = 0;
	searchName = '';
	selectedStatus = '';
	startDate = '';
	endDate = '';

	// Cursos (placeholder inicial)
	misCursos: any[] = [];
	misCursosProgreso: any[] = [];

	constructor(
		private apiService: APIService,
		private productsService: ProductsService,
		private mensajes: MensajesService,
		private router: Router,
		public dialog: MatDialog,
	) {}

	ngOnInit(): void {
		const raw = localStorage.getItem('currentUser');
		if (!raw) {
			this.router.navigate(['/auth/login']);
			return;
		}

		try {
			const user = JSON.parse(raw);
			this.idUsuario = user?.id ?? null;
		} catch {
			this.idUsuario = null;
		}

		if (!this.idUsuario) {
			this.router.navigate(['/auth/login']);
			return;
		}

		this.cargarPerfil();
		this.initPasswordForm();
		// Placeholders de cursos/órdenes (listados reales pueden integrarse luego)
		this.filteredOrders = this.saleOrders;
		this.loadPagedOrders();
		this.updateTotalPages();
	}

	// Perfil
	cargarPerfil(): void {
		if (!this.idUsuario) return;
		
		// Usar getProfile() en lugar de getById() para obtener todos los datos incluyendo profileFileUrl
		this.apiService.getProfile().subscribe({
			next: (data: any) => {
				this.datosUsuario = data || {};
				
				// Intentar obtener la imagen en este orden:
				// 1. profileFileUrl (si es URL válida)
				// 2. profileImage.base64 (si existe)
				// 3. Default
				let imageUrl = this.datosUsuario.profileFileUrl;
				
				// Validar que sea una URL válida (no una ruta del servidor)
				if (imageUrl && (imageUrl.startsWith('/var/') || imageUrl.startsWith('/usr/') || /^\d+(\.\d+)?$/.test(imageUrl))) {
					imageUrl = null;
				}
				
				// Si no hay URL válida, usar base64
				if (!imageUrl && this.datosUsuario.profileImage?.base64) {
					imageUrl = `data:image/jpeg;base64,${this.datosUsuario.profileImage.base64}`;
				}
				
				// Asignar la URL final o usar imagen por defecto
				this.datosUsuario.profileFileUrl = imageUrl || 'assets/images/user.png';
			},
			error: () => {
				Swal.fire('Error', 'No se pudo cargar tu perfil', 'error');
			}
		});
	}

	toggleMenu(): void {
		this.menuAbierto = !this.menuAbierto;
	}

	setNombreMenu(nombre: string): void {
		this.nombreMenuSeleccionado = nombre;
		this.menuAbierto = false;
	}

	selectTab(index: number): void {
		this.selectedTab = index;
		const tabNames = ['Mi Perfil', 'Mis Cursos', 'Mis Compras', 'Contraseña'];
		this.nombreMenuSeleccionado = tabNames[index] || 'Mi Perfil';
		this.menuAbierto = false; // Cerrar menu mobile
	}

	toggleEditMode(): void {
		if (this.editMode) {
			this.onSubmit();
		} else {
			this.editMode = true;
		}
	}

	onSubmit(): void {
		if (!this.idUsuario) return;
		const body = {
			firstName: this.datosUsuario.firstName,
			lastName: this.datosUsuario.lastName,
			displayName: `${this.datosUsuario.firstName || ''} ${this.datosUsuario.lastName || ''}`.trim(),
			phoneNumber: this.datosUsuario.phoneNumber,
			biography: this.datosUsuario.biography,
		};

		Swal.fire({ title: '¿Guardar cambios?', showCancelButton: true, confirmButtonText: 'Confirmar' })
			.then(res => {
				if (res.isConfirmed) {
					this.productsService.updateProfile(this.idUsuario!, body).subscribe({
						next: () => {
							Swal.fire('Datos actualizados', '', 'success');
							this.editMode = false;
							// Refrescar perfil por si backend formatea
							this.cargarPerfil();
							// Actualizar localStorage básico
							try {
								const current = JSON.parse(localStorage.getItem('currentUser') || '{}');
								const updated = { ...current, ...body };
								localStorage.setItem('currentUser', JSON.stringify(updated));
							} catch {}
						},
						error: () => Swal.fire('Error', 'No se pudo actualizar tu perfil', 'error')
					});
				}
			});
	}

	// Imagen de perfil
	triggerFileInput(): void {
		this.fileInput?.nativeElement?.click();
	}

	onFileSelected(event: any): void {
		const file: File = event?.target?.files?.[0];
		if (!file || !this.idUsuario) return;

		const MAX_MB = 5 * 1024 * 1024;
		if (file.size > MAX_MB) {
			Swal.fire('Archivo demasiado grande', 'Tamaño máximo 5MB', 'error');
			event.target.value = '';
			return;
		}

		// Validar archivo con utilidad del servicio
		const v = this.productsService.validateProfileImageFile(file);
		if (!v.valid) {
			Swal.fire('Archivo inválido', v.error || 'Formato o tamaño no permitido', 'error');
			event.target.value = '';
			return;
		}

		const formData = new FormData();
		formData.append('photo', file); // Cambio de 'file' a 'photo' para la nueva API

		Swal.fire({ title: 'Subiendo imagen...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
		
		// Usar la nueva API auth/profile/photo
		this.productsService.uploadProfilePhoto(formData).subscribe({
			next: (response: any) => {
				Swal.close();
				
				// El backend devuelve la URL de la imagen directamente
				const imageUrl = response?.url || response?.profileFileUrl || response?.ImageUrl;
				if (!imageUrl) {
					Swal.fire('Error', 'No se recibió URL de imagen', 'error');
					return;
				}

				// Actualizar en memoria
				this.datosUsuario.profileFileUrl = imageUrl;
				
				// Actualizar localStorage PRESERVANDO datos existentes como token
				try {
					const existingData = JSON.parse(localStorage.getItem('currentUser') || '{}');
					
					const userDataForStorage = {
						...existingData // Preservar token y otros datos existentes
					};
					
					// Solo guardar si NO es base64
					if (!imageUrl.startsWith('data:')) {
						userDataForStorage.imgProfile = imageUrl;
						userDataForStorage.profileFileUrl = imageUrl;
					} else {
						// Si es base64, solo guardar metadatos
						delete userDataForStorage.imgProfile;
						userDataForStorage.profileImage = { hasBase64: true };
						
						// Guardar imagen en sessionStorage
						try {
							sessionStorage.setItem('profileImageBase64', imageUrl);
						} catch (e) {
							console.warn('⚠️ [STUDENT-PROFILE-UPLOAD] No se pudo guardar imagen en sessionStorage:', e);
						}
					}
					
					localStorage.setItem('currentUser', JSON.stringify(userDataForStorage));
				} catch (e) {
					console.error('❌ [STUDENT-PROFILE-UPLOAD] Error al actualizar localStorage:', e);
				}
				
				Swal.fire('Éxito', 'Imagen de perfil actualizada', 'success');
			},
			error: (err) => {
				Swal.close();
				const msg = err?.message || 'No se pudo subir la imagen';
				Swal.fire('Error', msg, 'error');
			}
		});
	}

	// Contraseña: enviar email de restablecimiento
	initPasswordForm(): void {
		this.passwordForm = new FormGroup({
			email: new FormControl('', [Validators.required, Validators.email])
		});
		// Prefill con email del perfil si existe
		const email = this.datosUsuario?.email || JSON.parse(localStorage.getItem('currentUser') || '{}')?.email;
		if (email) this.passwordForm.patchValue({ email });
	}

	sendResetPassword(): void {
		if (this.passwordForm.invalid) return;
		const email = this.passwordForm.value.email;
		this.apiService.forgotPassword({ email }).subscribe({
			next: () => Swal.fire('Listo', 'Te enviamos un correo para restablecer tu contraseña.', 'success'),
			error: () => Swal.fire('Error', 'No se pudo enviar el correo de restablecimiento.', 'error')
		});
	}

	// Compras (paginación client-side con placeholders)
	loadPagedOrders(): void {
		const start = (this.pagina - 1) * this.ordersPerPage;
		const end = start + this.ordersPerPage;
		this.pagedOrders = (this.filteredOrders || []).slice(start, end);
	}

	updateTotalPages(): void {
		this.totalPages = Math.max(1, Math.ceil((this.filteredOrders || []).length / this.ordersPerPage));
	}

	paginarCompras(dir: 'suma' | 'resta'): void {
		if (dir === 'suma' && this.pagina < this.totalPages) this.pagina++;
		else if (dir === 'resta' && this.pagina > 1) this.pagina--;
		this.loadPagedOrders();
	}

	// Sesión
	logOut(): void {
		Swal.fire({ title: '¿Seguro quieres cerrar sesión?', showCancelButton: true, confirmButtonText: 'Confirmar' })
			.then(res => {
				if (res.isConfirmed) {
					try { localStorage.removeItem('currentUser'); } catch {}
					this.router.navigate(['/auth/login']);
					Swal.fire('Sesión cerrada', '', 'success');
				}
			});
	}
}

